import { CernerRole, Responsibility, UserRole } from '@lmc/ts-common'
import { getNominative } from 'common/utils'
import { isGroupUser as checkIsGroupUser } from 'utils'

const ROLE_OPTIONS_CAREHOME_BASE = [
  { label: 'Carer', value: UserRole.carer },
  { label: 'Senior Carer', value: UserRole.seniorCarer }
]

const MANAGEMENT_ROLE = { label: 'Management', value: UserRole.management }
const ADMIN_ROLE = { label: 'Admin', value: UserRole.careHomeAdmin }
const AUDIT_VIEW_ROLE = { label: 'Audit View', value: UserRole.auditView }
const IT_SUPPORT_ROLE = { label: 'IT', value: UserRole.it }

const ROLE_OPTIONS_GROUP = [
  { label: 'Group Admin', value: UserRole.groupAdmin },
  { label: 'Group Manager', value: UserRole.groupManager }
]

const ROLE_NOTE_CAREHOME = (preferredPeopleNominative: string) =>
  `Carers can only access the Carer App, while Senior Carers also have limited access to the Care Office (viewing ${getNominative(
    preferredPeopleNominative || '',
    'Resident'
  )} and Report information only). Admins have full access to the Care Office.`
const ROLE_NOTE_GROUP =
  'Group Admins can fully access the group dashboard. Group Managers will not be able to edit any information.'

export const getRoleFieldsProps = (
  initial?: Record<string, unknown> | undefined,
  role?: UserRole,
  preferredPeopleNominative = '',
  managementRoleEnabled = false,
  auditViewRoleEnabled = false,
  itSupportRoleEnabled = false
) => {
  const isGroupUser = checkIsGroupUser(initial?.role as string | undefined)

  const careHomeOptions = [...ROLE_OPTIONS_CAREHOME_BASE]
  if (managementRoleEnabled) {
    careHomeOptions.push(MANAGEMENT_ROLE)
  }
  careHomeOptions.push(ADMIN_ROLE)
  if (auditViewRoleEnabled) {
    careHomeOptions.push(AUDIT_VIEW_ROLE)
  }

  if (itSupportRoleEnabled) {
    careHomeOptions.push(IT_SUPPORT_ROLE)
  }

  const viewerIsManagement = role === UserRole.management
  const targetIsAdmin = initial?.role === UserRole.careHomeAdmin

  // Management viewers: admin option is disabled (can't promote to admin).
  // If the target IS already an admin, management can't demote them either —
  // the entire field is locked (roleFieldDisabled) so all options are inert.
  const visibleCareHomeOptions = viewerIsManagement
    ? careHomeOptions.map((o) =>
        o.value === UserRole.careHomeAdmin ? { ...o, disabled: true } : o
      )
    : careHomeOptions

  const roleFieldDisabled = viewerIsManagement && targetIsAdmin

  const ROLE_OPTIONS = isGroupUser ? ROLE_OPTIONS_GROUP : visibleCareHomeOptions

  const ROLE_NOTE = isGroupUser
    ? ROLE_NOTE_GROUP
    : ROLE_NOTE_CAREHOME(preferredPeopleNominative)

  return { ROLE_NOTE, ROLE_OPTIONS, roleFieldDisabled, isGroupUser }
}

// Returns false when the viewer should not be permitted to make destructive
// edits (email, status, site access) to the target — i.e. the target holds a
// role with higher privilege than the viewer. Add new restricted pairs here as
// additional roles are introduced.
export const canViewerEditAccessAndPermissionsOnTarget = (
  viewerRole?: UserRole,
  targetRole?: UserRole
): boolean => {
  if (
    viewerRole === UserRole.management &&
    targetRole === UserRole.careHomeAdmin
  ) {
    return false
  }
  return true
}

export const CARE_MANAGEMENT_LABEL = 'Care Management'

export const CERNER_ROLE_OPTIONS = [
  { value: CernerRole.NoAccess, label: 'No Access' },
  { value: CernerRole.SeniorCarer, label: 'Senior Carer Access' },
  { value: CernerRole.Admin, label: 'Admin Access' },
  { value: CernerRole.RegisteredManager, label: 'Registered Manager Access' }
]

export const getPermissionBooleanByTemplate = (
  label?: string,
  templates?: { id: string; label: string }[]
) => templates?.find((item) => item.label === label)

export const SAFEGUARDING_RADIO_OPTIONS = [
  { value: Responsibility.SafeguardingOfficer, label: 'Yes' },
  { value: null, label: 'No' }
] as const

export const getSafeguardingPermissionValue = (
  permissions: string[] = []
): string | null => {
  return permissions.includes(Responsibility.SafeguardingOfficer)
    ? Responsibility.SafeguardingOfficer
    : null
}

export const updatePermissionsForSafeguarding = (
  currentPermissions: string[] = [],
  shouldHavePermission: boolean
): string[] => {
  if (shouldHavePermission) {
    return currentPermissions.includes(Responsibility.SafeguardingOfficer)
      ? currentPermissions
      : [...currentPermissions, Responsibility.SafeguardingOfficer]
  }

  return currentPermissions.filter(
    (permission) => permission !== Responsibility.SafeguardingOfficer
  )
}
